import React, { useEffect, useRef, useState } from "react";
import "./WhyChoose.css";
import AOS from "aos";
import "aos/dist/aos.css";

const features = [
  {
    icon: "🏥",
    title: "Sterile Environment Solutions",
    desc: "Leading manufacturer, exporter and supplier of sterile environment solutions engineered to eliminate infection risk in critical care spaces.",
    keyword: "sterile modular operation theatre solutions",
  },
  {
    icon: "⚙️",
    title: "Inhouse Design & Development",
    desc: "Every product is designed and developed in-house to match international healthcare standards — fully customised for your facility.",
    keyword: "custom modular OT manufacturer India",
  },
  {
    icon: "🛡️",
    title: "Superior Product Quality",
    desc: "High performance, corrosion-resistant, durable and reliable. Built with premium raw materials and compact custom-made designs.",
    keyword: "high quality modular operation theatre manufacturer",
  },
];

const stats = [
  { end: 170, suffix: "+", label: "OTs Installed" },
  { end: 10, suffix: "+", label: "Years Experience" },
  { end: 130, suffix: "+", label: "Hospital Clients" },
];

/* Animated counter hook */
function useCounter(end, duration = 1800, start = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setCount(Math.floor(eased * end));

      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [start, end, duration]);

  return count;
}

/* Single stat item */
function StatItem({ stat, start }) {
  const count = useCounter(stat.end, 1800, start);

  return (
    <div className="wc-stat">
      <strong>
        {count}
        {stat.suffix}
      </strong>
      <span>{stat.label}</span>
    </div>
  );
}

/* Particle canvas */
function ParticleCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let animId;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 36 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.8 + 0.4,
      dx: (Math.random() - 0.5) * 0.35,
      dy: (Math.random() - 0.5) * 0.35,
      alpha: Math.random() * 0.5 + 0.15,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,229,255,${p.alpha})`;
        ctx.fill();

        p.x += p.dx;
        p.y += p.dy;

        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dist = Math.hypot(
            particles[i].x - particles[j].x,
            particles[i].y - particles[j].y,
          );

          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0,188,212,${0.12 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="wc-particles" />;
}

/* Main component */
function WhyChoose() {
  const [statsStarted, setStatsStarted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 1000, easing: "ease-out-cubic", once: true });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsStarted(true);
      },
      { threshold: 0.3 },
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  /* Auto mobile slider */
  useEffect(() => {
    const slider = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % features.length);
    }, 3000);

    return () => clearInterval(slider);
  }, []);

  return (
    <section
      className="why-choose"
      id="why-choose"
      ref={sectionRef}
      aria-label="Why Choose Wellness Electromedical Systems Pvt Ltd WEMS Modular OT Manufacturer India"
    >
      <span className="seo-hidden">
        Wellness Electromedical Systems Pvt Ltd. WEMS is a leading Modular OT
        manufacturer in India specializing in Modular Operation Theatre
        solutions, ICU, NICU, PICU and hospital infrastructure systems.
      </span>

      <div className="wc-container">
        {/* Left Main Card */}
        <div className="wc-main" data-aos="fade-right">
          <ParticleCanvas />

          <div className="wc-main-inner">
            <div className="wc-main-tag">Trusted Since 2014</div>

            <h2 className="wc-main-heading">
              Why Choose <span>Wellness Electromedical Systems Pvt Ltd.</span>
            </h2>

            <div className="seo-hidden">
              Modular OT Manufacturer | Modular Operation Theatre Company India
            </div>

            <p className="wc-main-text">
              Wellness Electromedical Systems Pvt Ltd. (WEMS) designs and
              manufactures Modular Operation Theatre systems, ICU, NICU, PICU,
              recovery units and hospital infrastructure solutions across India.
            </p>

            <div className="wc-stats">
              {stats.map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div className="wc-stat-divider" />}
                  <StatItem stat={s} start={statsStarted} />
                </React.Fragment>
              ))}
            </div>

            <a href="/contact-modular-ot" className="wc-btn">
              Modular OT Consultation →
            </a>
          </div>
        </div>

        {/* Right Feature Cards */}
        <div className="wc-cards">
          {features.map((f, i) => (
            <div
              className={`wc-card mobile-slide ${i === currentSlide ? "active" : ""}`}
              key={i}
              data-aos="fade-left"
              data-aos-delay={i * 120}
              aria-label={f.keyword}
            >
              <div className="wc-card-glow" />
              <div className="wc-card-line" />

              <div className="wc-card-icon">
                <span>{f.icon}</span>
              </div>

              <div className="wc-card-body">
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhyChoose;
